import { SimpleOption } from '../../../../../components/Option'
import { controlList } from '../../../storage'
import writeXlsxFile from 'write-excel-file'
import { get } from '../../../../../utilities/http'
import { buildPath } from '../../../../../sections/ModalPrint/utils'
import {
    ShippingCostReportExcel,
    AdditionalInternationalCost
} from '../../../types'
import Notify from '../../../../../utilities/Notify'
import moment from 'moment'

export default function OptionExcel() {
    async function download() {
        try {
            const companyId = controlList.filter.getParam('company_id')
            const startDate = moment(
                controlList.filter.getParam('start_date')
            ).format('YYYY-MM-DD')
            const endDate = moment(
                controlList.filter.getParam('end_date')
            ).format('YYYY-MM-DD')

            const { data } = await get<ShippingCostReportExcel[]>(
                buildPath('/v5.6/apis/shipping_cost_report/excel', [
                    ['company_id', companyId],
                    ['start_date', startDate],
                    ['end_date', endDate]
                ])
            )

            const rows = data?.result ?? []
            if (!rows.length) {
                throw new Error('Respuesta vacía del servicio')
            }

            const additionalConcepts = new Map<number, string>()
            rows.forEach((row: ShippingCostReportExcel) => {
                if (row.additional_international_costs) {
                    row.additional_international_costs.forEach(cost => {
                        if (!additionalConcepts.has(cost.concept_id)) {
                            additionalConcepts.set(
                                cost.concept_id,
                                cost.concept_name
                            )
                        }
                    })
                }
            })
            const sortedConcepts = Array.from(
                additionalConcepts.entries()
            ).sort((a, b) => a[0] - b[0])

            // Definir los headers que irán en la primera columna (vertical)
            const headers = [
                'Fecha Inicial:',
                startDate,
                'Fecha Final:',
                endDate,
                '',
                'Tipo envío',
                `Número de embarque (${rows.length})`,
                'Mes',
                'Declaración',
                'Orden de compra (OC)',
                'Número de recepción (RC)',
                'Proveedor(es)',
                'Factura pre-liquidación',
                'Factura comercial',
                'Nacionalizador',
                'Documento nacionalizador',
                'Moneda',
                'FOB',
                'CIF',
                'Monto proveedor',
                'Bultos',
                'Peso',
                'Arancel',
                'Tasa',
                'ITBMS (pre-liquidación)',
                '',
                ...sortedConcepts.map(([, name]) => name),
                '',
                'Costos agente aduanal',
                'ITBMS',
                'Total factura agente PTY',
                'Monto a costear'
            ]

            // Preparar los datos de cada declaración para las columnas
            const declarationColumns = rows.map(
                (row: ShippingCostReportExcel) => {
                    // Determinar si usar valores en USD o en moneda local
                    const isUSD = row.currency_code === 'USD'
                    const currencyDisplay = row.currency_code || 'USD'

                    const additionalCostsMap = new Map<
                        number,
                        AdditionalInternationalCost
                    >()
                    if (row.additional_international_costs) {
                        row.additional_international_costs.forEach(cost => {
                            additionalCostsMap.set(cost.concept_id, cost)
                        })
                    }

                    return [
                        { value: '', align: 'center' }, // Fecha Inicial (vacío)
                        { value: '', align: 'center' }, // startDate (vacío)
                        { value: '', align: 'center' }, // Fecha Final (vacío)
                        { value: '', align: 'center' }, // endDate (vacío)
                        { value: '', align: 'center' }, // Espacio vacío
                        {
                            value: row.shipment_type || '',
                            type: String,
                            align: 'center',
                            wrap: true
                        }, // Tipo envío
                        {
                            value: row.shipment_number || '',
                            type: Number,
                            format: '0',
                            align: 'center'
                        }, // Número de embarque
                        {
                            value: row.month || '',
                            type: String,
                            align: 'center',
                            wrap: true
                        }, // Mes
                        {
                            value: row.declaration_number || '',
                            type: String,
                            align: 'center',
                            wrap: true
                        }, // Declaración
                        {
                            value: row.oc_codes || '',
                            type: String,
                            align: 'center',
                            wrap: true
                        }, // OC
                        {
                            value: row.reception_codes || '',
                            type: String,
                            align: 'center',
                            wrap: true
                        }, // Número de recepción (RC)
                        {
                            value: row.provider_names || '',
                            type: String,
                            align: 'center',
                            wrap: true
                        }, // Proveedor(es)
                        {
                            value: row.pre_liquidation_invoice_number || '',
                            type: String,
                            align: 'center',
                            wrap: true
                        }, // Factura pre-liquidación
                        {
                            value: row.commercial_invoice_number || '',
                            type: String,
                            align: 'center',
                            wrap: true
                        }, // Factura comercial
                        {
                            value: row.nationalizer_name || '',
                            type: String,
                            align: 'center',
                            wrap: true
                        }, // Nacionalizador
                        {
                            value: row.nationalizer_documents || '',
                            type: String,
                            align: 'center',
                            wrap: true
                        }, // Documento nacionalizador
                        {
                            value: currencyDisplay,
                            type: String,
                            align: 'center',
                            wrap: true
                        }, // Moneda
                        {
                            value: isUSD ? row.fob_dolar : row.fob_local,
                            type: Number,
                            format: '0.00'
                        }, // FOB
                        {
                            value: isUSD ? row.cif_original : row.cif_local,
                            type: Number,
                            format: '0.00'
                        }, // CIF
                        {
                            value: isUSD
                                ? row.provider_amount_usd
                                : row.provider_amount_local,
                            type: Number,
                            format: '0.00'
                        }, // Monto proveedor
                        { value: row.packages, type: Number, format: '0' }, // Bultos
                        {
                            value: row.weight || 0,
                            type: Number,
                            format: '0'
                        }, // Peso
                        {
                            value: isUSD ? row.duty_original : row.duty_local,
                            type: Number,
                            format: '0.00'
                        }, // Arancel
                        {
                            value: isUSD ? row.rate_original : row.rate_local,
                            type: Number,
                            format: '0.00'
                        }, // Tasa
                        {
                            value: isUSD
                                ? row.itbms_products_original
                                : row.itbms_products_local,
                            type: Number,
                            format: '0.00'
                        }, // ITBMS (productos)
                        { value: '', align: 'center' }, // Espacio vacío
                        ...sortedConcepts.map(([conceptId]) => {
                            const cost = additionalCostsMap.get(conceptId)
                            return {
                                value: cost
                                    ? isUSD
                                        ? cost.original_cost
                                        : cost.local_cost
                                    : 0,
                                type: Number,
                                format: '0.00'
                            }
                        }), // Costos internacionales adicionales (dinámicos)
                        { value: '', align: 'center' }, // Espacio vacío
                        {
                            value: isUSD
                                ? row.customs_agent_costs_original
                                : row.customs_agent_costs_local,
                            type: Number,
                            format: '0.00'
                        }, // Costos agente aduanal
                        {
                            value: isUSD
                                ? row.logistical_costs_iva_original
                                : row.logistical_costs_iva_local,
                            type: Number,
                            format: '0.00'
                        }, // ITBMS
                        {
                            value: isUSD
                                ? row.total_agent_invoice_original
                                : row.total_agent_invoice_local,
                            type: Number,
                            format: '0.00'
                        }, // Total factura agente PTY
                        {
                            value: row.amount_to_cost,
                            type: Number,
                            format: '0.00'
                        } // Monto a costear
                    ]
                }
            )

            // Construir las filas con formato horizontal
            // Cada fila tendrá: [header, dato1, dato2, dato3, ...]
            const excelData: any[][] = []

            for (let i = 0; i < headers.length; i++) {
                const row: any[] = []

                // Primera columna: header con formato especial para algunos
                if (i === 0 || i === 2) {
                    // Fecha Inicial/Final labels
                    row.push({
                        value: headers[i],
                        fontWeight: 'bold' as const,
                        backgroundColor: '#e7e6e6'
                    })
                } else if (i === 1 || i === 3) {
                    // Fechas
                    row.push({
                        value: headers[i],
                        backgroundColor: '#f2f2f2'
                    })
                } else if (i >= 5) {
                    // Headers de datos (desde índice 5 en adelante)
                    row.push({
                        value: headers[i],
                        fontWeight: 'bold' as const
                    })
                } else {
                    // Espacio vacío
                    row.push({ value: headers[i] })
                }

                // Agregar los datos de cada declaración en columnas
                declarationColumns.forEach(col => {
                    row.push(col[i] || { value: '' })
                })

                excelData.push(row)
            }

            // Configurar anchos de columnas
            // Primera columna para headers, luego una columna por cada declaración
            const columns = [
                { width: 25 }, // Columna de headers
                ...Array(declarationColumns.length).fill({ width: 30 }) // Columnas de datos
            ]

            const fileName = `reporte_de_costos_por_embarque_${startDate}_${endDate}.xlsx`

            await writeXlsxFile(excelData, {
                fileName,
                columns
            })

            Notify.success('El archivo Excel se ha descargado correctamente')
        } catch (error) {
            Notify.error(
                'No se pudo descargar el reporte. Por favor, intenta nuevamente.'
            )
        }
    }

    return <SimpleOption onClick={download}>Exportar en Excel</SimpleOption>
}
